import { createClient } from '@supabase/supabase-js';
import cookieStorageAdapter, { migrateLocalStorageToCookie } from './cookieStorageAdapter';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const AUTH_RECOVERY_FLOW_TYPE = 'recovery';
const PENDING_AUTH_FLOW_MAX_AGE_MS = 10 * 60 * 1000;

export const PENDING_AUTH_FLOW_STORAGE_KEY = 'mpt_pending_auth_flow';
let pendingAuthFlowMemory = null;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase configuration is missing. Check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

// Clé de stockage Supabase (utilisée pour la migration localStorage → cookie)
const getSupabaseStorageKey = () => {
  try {
    if (!supabaseUrl) return 'sb-auth-token';
    const hostname = new URL(supabaseUrl).hostname;
    const projectRef = hostname.split('.')[0];
    return projectRef ? `sb-${projectRef}-auth-token` : 'sb-auth-token';
  } catch {
    return 'sb-auth-token';
  }
};

const getAuthFlowTypeFromCurrentUrl = () => {
  if (typeof window === 'undefined') return null;

  try {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const searchParams = new URLSearchParams(window.location.search);
    return hashParams.get('type') || searchParams.get('type');
  } catch {
    return null;
  }
};

const getAuthFlowStorage = () => {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
};

const createPendingAuthFlowRecord = (flowType) => ({
  type: flowType,
  createdAt: Date.now(),
});

const isFreshPendingAuthFlowRecord = (record) => {
  const createdAt = Number(record?.createdAt || 0);
  return (
    record?.type === AUTH_RECOVERY_FLOW_TYPE &&
    createdAt > 0 &&
    Date.now() - createdAt <= PENDING_AUTH_FLOW_MAX_AGE_MS
  );
};

export const rememberPendingAuthFlowType = (flowType) => {
  if (flowType !== AUTH_RECOVERY_FLOW_TYPE) return;

  const record = createPendingAuthFlowRecord(flowType);
  pendingAuthFlowMemory = record;

  const storage = getAuthFlowStorage();
  if (!storage) return;

  try {
    storage.setItem(PENDING_AUTH_FLOW_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore storage errors
  }
};

export const getPendingAuthFlowType = () => {
  const storage = getAuthFlowStorage();

  if (storage) {
    try {
      const raw = storage.getItem(PENDING_AUTH_FLOW_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (isFreshPendingAuthFlowRecord(parsed)) {
          return parsed.type;
        }

        storage.removeItem(PENDING_AUTH_FLOW_STORAGE_KEY);
      }
    } catch {
      try {
        storage.removeItem(PENDING_AUTH_FLOW_STORAGE_KEY);
      } catch {
        // ignore
      }
    }
  }

  if (pendingAuthFlowMemory) {
    if (isFreshPendingAuthFlowRecord(pendingAuthFlowMemory)) {
      return pendingAuthFlowMemory.type;
    }
    pendingAuthFlowMemory = null;
  }

  return null;
};

export const clearPendingAuthFlowType = () => {
  pendingAuthFlowMemory = null;

  const storage = getAuthFlowStorage();
  if (!storage) return;

  try {
    storage.removeItem(PENDING_AUTH_FLOW_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const rememberPendingAuthFlowFromCurrentUrl = () => {
  const flowType = getAuthFlowTypeFromCurrentUrl();
  rememberPendingAuthFlowType(flowType);
};

// Migration automatique : localStorage → cookie (une seule fois)
if (typeof window !== 'undefined') {
  rememberPendingAuthFlowFromCurrentUrl();

  if (supabaseUrl) {
    migrateLocalStorageToCookie(getSupabaseStorageKey());
  }
}

// Options du client Supabase pour une persistance via cookie host-only de courte duree
const supabaseOptions = {
  auth: {
    persistSession: true,
    storage: typeof window !== 'undefined' ? cookieStorageAdapter : undefined,
    // Rafraîchir automatiquement les tokens avant expiration
    autoRefreshToken: true,
    // Détecter automatiquement les changements de session dans d'autres onglets
    detectSessionInUrl: true,
  },
};

// Créer le client Supabase seulement si les variables sont définies
// Sinon, créer un mock qui ne bloque pas l'app
export const supabase = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey, supabaseOptions)
  : {
      auth: {
        getSession: async () => ({ data: { session: null }, error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        signUp: async () => ({ data: null, error: new Error('Supabase not configured') }),
        signInWithPassword: async () => ({ data: null, error: new Error('Supabase not configured') }),
        resetPasswordForEmail: async () => ({ data: null, error: new Error('Supabase not configured') }),
        updateUser: async () => ({ data: null, error: new Error('Supabase not configured') }),
        signOut: async () => ({ error: null }),
      },
      from: () => ({
        select: () => ({ eq: () => ({ maybeSingle: async () => ({ data: null, error: null }), single: async () => ({ data: null, error: null }) }) }),
        insert: () => ({ select: () => ({ single: async () => ({ data: null, error: null }) }) }),
        update: () => ({ eq: () => ({ select: () => ({ maybeSingle: async () => ({ data: null, error: null }) }) }) }),
      }),
    };
